import React from 'react';

import { IconRegistry } from 'js/src/agent/registries/IconRegistry';
import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import { VoiceoverRegistry } from 'js/src/agent/registries/VoiceoverRegistry';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import AudioRegistryDisplay from 'js/src/components/conversation/AudioRegistryDisplay';
import IconRegistryDisplay from 'js/src/components/conversation/IconRegistryDisplay';
import MediaRegistryDisplay from 'js/src/components/conversation/MediaRegistryDisplay';

interface RegistriesModalProps {
    showing: boolean;
    onHide: VoidFunction;
}

const RegistriesModal: React.FC<RegistriesModalProps> = ({ showing, onHide }) => {
    // Registries are mutable singletons, not reactive state. Re-read them each
    // time the modal opens so a developer always sees the current contents.
    const snapshot = React.useMemo(() => {
        if (!showing) {
            return null;
        }
        return {
            media: MediaRegistry.getInstance().serialize(),
            music: MusicRegistry.getInstance().serialize(),
            voiceover: VoiceoverRegistry.getInstance().serialize(),
            icon: IconRegistry.getInstance().serialize(),
        };
    }, [showing]);

    const isEmpty =
        snapshot != null &&
        snapshot.media.items.length === 0 &&
        snapshot.music.items.length === 0 &&
        snapshot.voiceover.items.length === 0 &&
        snapshot.icon.items.length === 0;

    return (
        <Modal
            showing={showing}
            onHidden={onHide}
            title="Registries"
            size={MODAL_SIZES.LG}
            scrollable
        >
            {snapshot && (
                <>
                    <MediaRegistryDisplay mediaRegistry={snapshot.media} />
                    <AudioRegistryDisplay
                        title="Music Registry"
                        emoji="🎵"
                        registry={snapshot.music}
                    />
                    <AudioRegistryDisplay
                        title="Voiceover Registry"
                        emoji="🎙️"
                        registry={snapshot.voiceover}
                    />
                    <IconRegistryDisplay iconRegistry={snapshot.icon} />
                    {isEmpty && (
                        <div className="text-muted text-center py-l5-24">
                            All registries are empty
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default RegistriesModal;
